import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Heart, Clock, Swords, Trophy, X, Volume2, VolumeX, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import PokemonImage from '../components/PokemonImage';
import { getMonsterGif, getMonsterGifByMonster, getMonsterFallbackGif, getMonsterDisplayName, loadUploadedGifs } from '../utils/helpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Sound effects URLs (using free sounds)
const SOUNDS = {
  battle_music: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3',
  correct: 'https://assets.mixkit.co/active_storage/sfx/2000/2000-preview.mp3',
  wrong: 'https://assets.mixkit.co/active_storage/sfx/2955/2955-preview.mp3',
  damage: 'https://assets.mixkit.co/active_storage/sfx/2020/2020-preview.mp3',
  victory: 'https://assets.mixkit.co/active_storage/sfx/1435/1435-preview.mp3',
  defeat: 'https://assets.mixkit.co/active_storage/sfx/2018/2018-preview.mp3',
  tick: 'https://assets.mixkit.co/active_storage/sfx/2572/2572-preview.mp3'
};

// Pre-load images to prevent loading issues during duel
const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(src);
    img.onerror = () => reject(src);
    img.src = src;
  });
};

const DuelArena = () => {
  const { lobbyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Match state
  const [matchData, setMatchData] = useState(location.state?.matchData || null);
  const [loading, setLoading] = useState(!location.state?.matchData);
  const [gifsLoaded, setGifsLoaded] = useState(false);
  const [waitingForHost, setWaitingForHost] = useState(!location.state?.matchData);
  const [hostName, setHostName] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeLeft, setTimeLeft] = useState(45);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [questionResult, setQuestionResult] = useState(null);
  
  // Player states
  const [player1Hearts, setPlayer1Hearts] = useState(5);
  const [player2Hearts, setPlayer2Hearts] = useState(5);
  const [player1Damage, setPlayer1Damage] = useState(false);
  const [player2Damage, setPlayer2Damage] = useState(false);
  
  // Match end state
  const [matchEnded, setMatchEnded] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  
  // Audio state
  const [soundEnabled, setSoundEnabled] = useState(true);
  const audioRef = useRef(null);
  const effectsRef = useRef({});
  
  // Timer ref + question lifecycle refs
  const timerIntervalRef = useRef(null);
  const timerEndAtRef = useRef(0);
  const autoSubmitTimeoutRef = useRef(null);
  const resolutionPollRef = useRef(null);
  const resolutionTimeoutRef = useRef(null);
  const answerStartTime = useRef(null);
  const answerSubmittedRef = useRef(false);
  const matchDataRef = useRef(null);
  const currentQuestionRef = useRef(0);
  const handleSubmitAnswerRef = useRef(null);
  const pollForResolutionRef = useRef(null);
  const handleQuestionResultRef = useRef(null);

  // Keep latest values in refs (avoid stale-closure inside setInterval/setTimeout)
  useEffect(() => { answerSubmittedRef.current = answerSubmitted; }, [answerSubmitted]);
  useEffect(() => { matchDataRef.current = matchData; }, [matchData]);
  useEffect(() => { currentQuestionRef.current = currentQuestion; }, [currentQuestion]);

  // Get current student ID
  const [currentStudentId, setCurrentStudentId] = useState(null);

  // Load match data if not passed via state
  useEffect(() => {
    const loadMatch = async () => {
      if (matchData) {
        setLoading(false);
        setWaitingForHost(false);
        return;
      }
      
      try {
        // Poll for lobby status (for guests waiting for host to start)
        const pollForMatch = async () => {
          const response = await fetch(`${BACKEND_URL}/api/duels/lobby/${lobbyId}/status`, {
            credentials: 'include'
          });
          
          if (response.ok) {
            const data = await response.json();
            
            if (data.status === 'in_progress' && data.match_data) {
              // Match has started, set the data
              setMatchData(data.match_data);
              setLoading(false);
              setWaitingForHost(false);
              return true;
            } else if (data.status === 'waiting' || data.status === 'ready') {
              // Still waiting - continue polling
              setHostName(data.host_name);
              setLoading(false);
              setWaitingForHost(true);
              return false;
            } else {
              // Lobby finished or error
              toast.error('Lobby encerrado');
              navigate('/student/dashboard');
              return true;
            }
          } else {
            toast.error('Não foi possível carregar o duelo');
            navigate('/student/dashboard');
            return true;
          }
        };
        
        // Initial check
        const done = await pollForMatch();
        if (!done) {
          // Set up polling
          const pollInterval = setInterval(async () => {
            const done = await pollForMatch();
            if (done) {
              clearInterval(pollInterval);
            }
          }, 2000);
          
          // Clear on unmount
          return () => clearInterval(pollInterval);
        }
      } catch (error) {
        console.error('Failed to load match:', error);
        toast.error('Erro ao carregar duelo');
        navigate('/student/dashboard');
      }
    };
    
    loadMatch();
    
    // Get current student
    fetch(`${BACKEND_URL}/api/student/me`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.length > 0) {
          setCurrentStudentId(data[0].student_id);
        }
      })
      .catch(console.error);
  }, [lobbyId, matchData, navigate]);

  // Pre-load GIF cache and monster images when match data is available
  useEffect(() => {
    const preloadGifs = async () => {
      // First load the GIF cache
      await loadUploadedGifs();
      
      if (matchData) {
        const imagesToPreload = [];
        
        // Get monster GIFs for both players
        if (matchData.player1?.monster) {
          const p1Gif = getMonsterGifByMonster(matchData.player1.monster);
          if (p1Gif) imagesToPreload.push(p1Gif);
        }

        if (matchData.player2?.monster) {
          const p2Gif = getMonsterGifByMonster(matchData.player2.monster);
          if (p2Gif) imagesToPreload.push(p2Gif);
        }
        
        // Preload all images
        if (imagesToPreload.length > 0) {
          await Promise.allSettled(imagesToPreload.map(preloadImage));
        }
      }
      
      setGifsLoaded(true);
    };
    
    preloadGifs();
  }, [matchData]);

  // Initialize audio
  useEffect(() => {
    if (soundEnabled && matchData) {
      // Background music
      audioRef.current = new Audio(SOUNDS.battle_music);
      audioRef.current.loop = true;
      audioRef.current.volume = 0.3;
      audioRef.current.play().catch(() => {});
      
      // Preload effects
      Object.entries(SOUNDS).forEach(([key, url]) => {
        if (key !== 'battle_music') {
          effectsRef.current[key] = new Audio(url);
          effectsRef.current[key].volume = 0.5;
        }
      });
    }
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [soundEnabled, matchData]);

  // ─────────────────────────────────────────────────────────────────────
  // Wall-clock timer (robust against browser tab throttling)
  //
  // Problem: setInterval is heavily throttled (or paused) by the browser
  // when the tab is not visible. The previous implementation decremented
  // a counter every 1s — when the teacher (or the duel host) switched tabs,
  // their timer would "stick" while the opponent's kept moving, freezing
  // the match because resolve-question waits for BOTH submissions.
  //
  // Fix:
  //  - Compute remaining time from a wall-clock deadline (Date.now()).
  //  - The visual interval can be throttled without consequence (it just
  //    re-reads the deadline whenever the browser runs it).
  //  - A separate setTimeout fires the auto-submit at the absolute deadline.
  //    Background-throttled timers still fire eventually, and we also
  //    re-arm everything on `visibilitychange` to re-sync as soon as the
  //    tab is focused again.
  //  - Read state via refs to avoid stale closures.
  // ─────────────────────────────────────────────────────────────────────
  const clearActiveTimers = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (autoSubmitTimeoutRef.current) {
      clearTimeout(autoSubmitTimeoutRef.current);
      autoSubmitTimeoutRef.current = null;
    }
  }, []);

  const handleAutoSubmitTimeout = useCallback(() => {
    // Guard against double-submit (manual click after timeout / re-fire)
    if (answerSubmittedRef.current) return;
    handleSubmitAnswerRef.current && handleSubmitAnswerRef.current(null);
  }, []);

  useEffect(() => {
    if (loading || matchEnded || answerSubmitted) {
      clearActiveTimers();
      return;
    }

    const QUESTION_TIME_MS = 45000;
    answerStartTime.current = Date.now();
    timerEndAtRef.current = answerStartTime.current + QUESTION_TIME_MS;
    setTimeLeft(45);

    // Visual ticker: read remaining time from the wall-clock deadline.
    timerIntervalRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((timerEndAtRef.current - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 10 && remaining > 0 && soundEnabled && effectsRef.current.tick) {
        try {
          effectsRef.current.tick.currentTime = 0;
          effectsRef.current.tick.play().catch(() => {});
        } catch { /* ignore */ }
      }
      if (remaining <= 0) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }, 250);

    // Absolute auto-submit (still fires even if the visual interval is throttled).
    autoSubmitTimeoutRef.current = setTimeout(handleAutoSubmitTimeout, QUESTION_TIME_MS + 200);

    return () => {
      clearActiveTimers();
    };
  }, [currentQuestion, loading, matchEnded, answerSubmitted, soundEnabled, clearActiveTimers, handleAutoSubmitTimeout]);

  // Re-sync visual timer + auto-submit when the tab becomes visible again.
  // Without this, a tab that was hidden for >2 minutes could resume with a
  // stale `timeLeft` and miss the deadline by several seconds.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (matchEnded || answerSubmittedRef.current) return;
      const remainingMs = Math.max(0, timerEndAtRef.current - Date.now());
      setTimeLeft(Math.ceil(remainingMs / 1000));
      if (remainingMs <= 0) {
        handleAutoSubmitTimeout();
      } else {
        // Re-arm absolute auto-submit (some browsers cancel/throttle long
        // timeouts when tabs go inactive).
        if (autoSubmitTimeoutRef.current) clearTimeout(autoSubmitTimeoutRef.current);
        autoSubmitTimeoutRef.current = setTimeout(handleAutoSubmitTimeout, remainingMs + 200);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [matchEnded, handleAutoSubmitTimeout]);

  // Play sound effect
  const playSound = useCallback((soundName) => {
    if (soundEnabled && effectsRef.current[soundName]) {
      effectsRef.current[soundName].currentTime = 0;
      effectsRef.current[soundName].play().catch(() => {});
    }
  }, [soundEnabled]);

  // Submit answer (kept in a ref so timers using a stale closure still
  // call the latest version)
  const handleSubmitAnswer = useCallback(async (answer) => {
    const matchSnapshot = matchDataRef.current;
    const questionIndex = currentQuestionRef.current;
    if (answerSubmittedRef.current || !matchSnapshot) return;

    // Stop all active timers/intervals for this question
    clearActiveTimers();

    setSelectedAnswer(answer);
    setAnswerSubmitted(true);
    setWaitingForOpponent(true);

    const timeTaken = Date.now() - (answerStartTime.current || Date.now());

    try {
      const response = await fetch(`${BACKEND_URL}/api/duels/match/${matchSnapshot.match_id}/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          match_id: matchSnapshot.match_id,
          question_index: questionIndex,
          answer: answer || 'X', // X for timeout
          time_ms: timeTaken
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.correct) playSound('correct');
        else playSound('wrong');
        pollForResolutionRef.current && pollForResolutionRef.current();
      } else if (response.status === 400) {
        // Likely "already submitted" — proceed to poll for resolution anyway.
        pollForResolutionRef.current && pollForResolutionRef.current();
      } else {
        toast.error('Erro ao enviar resposta. Tentando novamente…');
        // Retry once after a short delay
        setTimeout(() => pollForResolutionRef.current && pollForResolutionRef.current(), 1500);
      }
    } catch (error) {
      console.error('Failed to submit answer:', error);
      toast.error('Conexão instável. Reconectando…');
      // Begin polling anyway — server-side timeout will resolve eventually.
      pollForResolutionRef.current && pollForResolutionRef.current();
    }
  }, [clearActiveTimers, playSound]);

  useEffect(() => { handleSubmitAnswerRef.current = handleSubmitAnswer; }, [handleSubmitAnswer]);

  // Poll for question resolution (robust against orphaned intervals)
  const pollForResolution = useCallback(() => {
    const matchSnapshot = matchDataRef.current;
    const questionIndex = currentQuestionRef.current;
    if (!matchSnapshot) return;

    // Cancel any previous polling for this match
    if (resolutionPollRef.current) clearInterval(resolutionPollRef.current);
    if (resolutionTimeoutRef.current) clearTimeout(resolutionTimeoutRef.current);

    const startedAt = Date.now();
    let consecutiveErrors = 0;

    const tick = async () => {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/duels/match/${matchSnapshot.match_id}/resolve-question?question_index=${questionIndex}`,
          { method: 'POST', credentials: 'include' }
        );

        if (response.ok) {
          consecutiveErrors = 0;
          const result = await response.json();

          if (result.status === 'continue' || result.status === 'match_ended') {
            if (resolutionPollRef.current) clearInterval(resolutionPollRef.current);
            if (resolutionTimeoutRef.current) clearTimeout(resolutionTimeoutRef.current);
            resolutionPollRef.current = null;
            resolutionTimeoutRef.current = null;
            handleQuestionResultRef.current && handleQuestionResultRef.current(result);
            return;
          }
          // status === 'waiting' → keep polling
        } else {
          consecutiveErrors += 1;
        }
      } catch (e) {
        consecutiveErrors += 1;
      }

      // If 5 consecutive network errors, surface a soft message but keep polling.
      if (consecutiveErrors === 5) {
        toast.error('Reconexão lenta… tentando novamente.', { id: 'duel-recon' });
      }
    };

    // Immediate poll, then every 1s.
    tick();
    resolutionPollRef.current = setInterval(tick, 1000);

    // Safety timeout: after 70s (15s past server-side deadline) stop and
    // notify. Server will normally auto-resolve before this triggers.
    resolutionTimeoutRef.current = setTimeout(() => {
      if (resolutionPollRef.current) clearInterval(resolutionPollRef.current);
      resolutionPollRef.current = null;
      const elapsed = Math.round((Date.now() - startedAt) / 1000);
      console.warn(`[Duel] Resolution poll timed out after ${elapsed}s`);
      toast.error('Não foi possível resolver a questão. Recarregue a página.');
    }, 70000);
  }, []);

  useEffect(() => { pollForResolutionRef.current = pollForResolution; }, [pollForResolution]);

  // Handle match end (declared before handleQuestionResult uses it)
  const handleMatchEnd = useCallback((result) => {
    setMatchEnded(true);
    setMatchResult(result);

    // Stop music
    if (audioRef.current) {
      audioRef.current.pause();
    }

    const isWinner = result.winner_id === currentStudentId;

    if (isWinner) {
      playSound('victory');
      confetti({
        particleCount: 200,
        spread: 100,
        origin: { y: 0.6 },
        colors: ['#FFD700', '#FFA500', '#FF6347']
      });
    } else {
      playSound('defeat');
    }
  }, [currentStudentId, playSound]);

  // Handle question result (kept in a ref for the same reason as submit)
  const handleQuestionResult = useCallback((result) => {
    setWaitingForOpponent(false);
    setQuestionResult(result);

    // Update hearts with animation (use functional setters to avoid stale state)
    setPlayer1Hearts(prev => {
      if (result.player1_hearts !== undefined && result.player1_hearts < prev) {
        setPlayer1Damage(true);
        playSound('damage');
        setTimeout(() => setPlayer1Damage(false), 500);
        return result.player1_hearts;
      }
      return result.player1_hearts !== undefined ? result.player1_hearts : prev;
    });
    setPlayer2Hearts(prev => {
      if (result.player2_hearts !== undefined && result.player2_hearts < prev) {
        setPlayer2Damage(true);
        playSound('damage');
        setTimeout(() => setPlayer2Damage(false), 500);
        return result.player2_hearts;
      }
      return result.player2_hearts !== undefined ? result.player2_hearts : prev;
    });

    if (result.status === 'match_ended') {
      handleMatchEnd(result);
    } else {
      setTimeout(() => {
        setCurrentQuestion(prev => prev + 1);
        setSelectedAnswer(null);
        setAnswerSubmitted(false);
        setQuestionResult(null);
      }, 3000);
    }
  }, [playSound, handleMatchEnd]);

  useEffect(() => { handleQuestionResultRef.current = handleQuestionResult; }, [handleQuestionResult]);

  // Cleanup ALL timers/intervals on unmount (prevents the duel from "ticking"
  // in the background after the student navigates away and creating leaked
  // requests that confuse the next match).
  useEffect(() => {
    return () => {
      clearActiveTimers();
      if (resolutionPollRef.current) clearInterval(resolutionPollRef.current);
      if (resolutionTimeoutRef.current) clearTimeout(resolutionTimeoutRef.current);
    };
  }, [clearActiveTimers]);

  // Toggle sound
  const toggleSound = () => {
    setSoundEnabled(prev => {
      if (prev && audioRef.current) {
        audioRef.current.pause();
      } else if (!prev && audioRef.current) {
        audioRef.current.play().catch(() => {});
      }
      return !prev;
    });
  };

  if (loading || (matchData && !gifsLoaded)) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <Swords className="w-16 h-16 text-red-500 animate-pulse mx-auto mb-4" />
          <p className="text-white text-xl">
            {loading ? 'Preparando arena...' : 'Carregando Pokémon...'}
          </p>
        </div>
      </div>
    );
  }

  // Waiting for host to start the match
  if (waitingForHost && !matchData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-800 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
            <Swords className="w-16 h-16 text-amber-500 mx-auto mb-4 animate-bounce" />
            <h2 className="text-2xl font-bold text-white mb-2">Sala de Duelo</h2>
            <p className="text-white/70 mb-6">
              Aguardando {hostName || 'o anfitrião'} iniciar o duelo...
            </p>
            <div className="flex items-center justify-center gap-2 text-amber-400 animate-pulse">
              <Clock className="w-5 h-5" />
              <span>Preparando batalha...</span>
            </div>
            <Button
              onClick={() => navigate('/student/dashboard')}
              variant="outline"
              className="mt-6 w-full border-white/30 text-white hover:bg-white/10"
            >
              Cancelar e Voltar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!matchData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <X className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-white text-xl">Duelo não encontrado</p>
          <Button onClick={() => navigate('/student/dashboard')} className="mt-4">
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  const currentQ = matchData.questions[currentQuestion];
  const isPlayer1 = currentStudentId === matchData.player1?.student_id;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Gym Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_transparent_0%,_rgba(0,0,0,0.8)_100%)]" />
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-amber-500/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-green-900/50 to-transparent" />
        {/* Arena lines */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-96 h-96 border-4 border-white/20 rounded-full" />
          <div className="absolute w-64 h-64 border-4 border-white/10 rounded-full" />
        </div>
      </div>

      {/* Sound Toggle */}
      <button
        onClick={toggleSound}
        className="absolute top-4 right-4 z-50 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
      >
        {soundEnabled ? (
          <Volume2 className="w-6 h-6 text-white" />
        ) : (
          <VolumeX className="w-6 h-6 text-white/50" />
        )}
      </button>

      {/* Match Header */}
      <div className="relative z-10 pt-6 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-4">
            <h1 className="text-2xl font-bold text-white flex items-center justify-center gap-2">
              <Swords className="w-6 h-6 text-red-500" />
              {matchData.duel_title}
            </h1>
            <p className="text-white/60 text-sm">Questão {currentQuestion + 1} de {matchData.questions.length}</p>
          </div>

          {/* Players Section */}
          <div className="flex justify-between items-center mb-8">
            {/* Player 1 */}
            <div className={`flex flex-col items-center ${player1Damage ? 'animate-shake' : ''}`}>
              <div className={`relative w-32 h-32 rounded-2xl overflow-hidden bg-gradient-to-br from-blue-500/30 to-blue-700/30 border-2 ${
                isPlayer1 ? 'border-yellow-400 ring-2 ring-yellow-400/50' : 'border-blue-400'
              } ${player1Damage ? 'animate-flash-red' : ''}`}>
                {matchData.player1?.monster ? (
                  <PokemonImage
                    src={getMonsterGifByMonster(matchData.player1.monster)}
                    fallbackSrc={getMonsterFallbackGif(matchData.player1.monster)}
                    alt={getMonsterDisplayName(matchData.player1.monster)}
                    className="w-full h-full object-contain p-2"
                    fallbackSize={80}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-4xl">🥚</div>
                )}
                {/* Shadow */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-16 h-4 bg-black/30 rounded-full blur-sm" />
              </div>
              <div className="mt-2 text-center">
                <p className="text-white font-bold text-sm">{matchData.player1?.name}</p>
                <p className="text-white/60 text-xs">Lv.{matchData.player1?.level}</p>
              </div>
              {/* Hearts */}
              <div className="flex gap-1 mt-2">
                {[...Array(5)].map((_, i) => (
                  <Heart
                    key={i}
                    className={`w-5 h-5 transition-all duration-300 ${
                      i < player1Hearts 
                        ? 'text-red-500 fill-red-500' 
                        : 'text-gray-600'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* VS */}
            <div className="text-4xl font-bold text-white/30">VS</div>

            {/* Player 2 */}
            <div className={`flex flex-col items-center ${player2Damage ? 'animate-shake' : ''}`}>
              <div className={`relative w-32 h-32 rounded-2xl overflow-hidden bg-gradient-to-br from-red-500/30 to-red-700/30 border-2 ${
                !isPlayer1 ? 'border-yellow-400 ring-2 ring-yellow-400/50' : 'border-red-400'
              } ${player2Damage ? 'animate-flash-red' : ''}`}>
                {matchData.player2?.monster ? (
                  <PokemonImage
                    src={getMonsterGifByMonster(matchData.player2.monster)}
                    fallbackSrc={getMonsterFallbackGif(matchData.player2.monster)}
                    alt={getMonsterDisplayName(matchData.player2.monster)}
                    className="w-full h-full object-contain p-2 transform scale-x-[-1]"
                    fallbackSize={80}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-4xl">🥚</div>
                )}
                {/* Shadow */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-16 h-4 bg-black/30 rounded-full blur-sm" />
              </div>
              <div className="mt-2 text-center">
                <p className="text-white font-bold text-sm">{matchData.player2?.name}</p>
                <p className="text-white/60 text-xs">Lv.{matchData.player2?.level}</p>
              </div>
              {/* Hearts */}
              <div className="flex gap-1 mt-2">
                {[...Array(5)].map((_, i) => (
                  <Heart
                    key={i}
                    className={`w-5 h-5 transition-all duration-300 ${
                      i < player2Hearts 
                        ? 'text-red-500 fill-red-500' 
                        : 'text-gray-600'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Question Area */}
      {!matchEnded && currentQ && (
        <div className="relative z-10 px-4 pb-8">
          <div className="max-w-2xl mx-auto">
            {/* Timer */}
            <div className="flex justify-center mb-4">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                timeLeft <= 10 ? 'bg-red-500/80 animate-pulse' : 'bg-white/10'
              }`}>
                <Clock className="w-5 h-5 text-white" />
                <span className={`font-bold text-xl ${timeLeft <= 10 ? 'text-white' : 'text-white/80'}`}>
                  {timeLeft}s
                </span>
              </div>
            </div>

            {/* Question Card */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
              <h2 className="text-white text-lg font-semibold mb-6 text-center">
                {currentQ.question_text}
              </h2>

              {/* Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {['A', 'B', 'C', 'D'].map((option) => {
                  const optionText = currentQ[`option_${option.toLowerCase()}`];
                  if (!optionText) return null;
                  
                  const isSelected = selectedAnswer === option;
                  const showResult = questionResult && answerSubmitted;
                  const isCorrect = showResult && questionResult.correct_answer === option;
                  
                  return (
                    <button
                      key={option}
                      onClick={() => !answerSubmitted && handleSubmitAnswer(option)}
                      disabled={answerSubmitted}
                      className={`p-4 rounded-xl text-left transition-all duration-300 ${
                        showResult
                          ? isCorrect
                            ? 'bg-green-500 border-green-400'
                            : isSelected
                              ? 'bg-red-500 border-red-400'
                              : 'bg-white/5 border-white/10'
                          : isSelected
                            ? 'bg-blue-500 border-blue-400 scale-105'
                            : 'bg-white/5 border-white/10 hover:bg-white/10 hover:scale-102'
                      } border-2`}
                    >
                      <span className="font-bold text-white mr-2">{option})</span>
                      <span className="text-white/90">{optionText}</span>
                    </button>
                  );
                })}
              </div>

              {/* Waiting indicator */}
              {waitingForOpponent && (
                <div className="mt-6 text-center text-white/60 animate-pulse">
                  Aguardando oponente responder...
                </div>
              )}

              {/* Question result */}
              {questionResult && !matchEnded && (
                <div className="mt-6 text-center">
                  <div className={`inline-block px-4 py-2 rounded-full ${
                    questionResult.loser_id === currentStudentId || questionResult.damage_reason === 'both_wrong'
                      ? 'bg-red-500/50'
                      : 'bg-green-500/50'
                  }`}>
                    <span className="text-white font-semibold">
                      {questionResult.damage_reason === 'both_wrong'
                        ? 'Ambos erraram! -1 ❤️ cada'
                        : questionResult.loser_id === currentStudentId
                          ? questionResult.damage_reason === 'slower'
                            ? 'Empate! Você foi mais lento! -1 ❤️'
                            : 'Resposta errada! -1 ❤️'
                          : questionResult.damage_reason === 'slower'
                            ? 'Empate! Oponente mais lento! +0 ❤️'
                            : 'Oponente errou! +0 ❤️'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Match End - Podium */}
      {matchEnded && matchResult && (
        <div className="relative z-10 flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center">
            {/* Winner/Loser Banner */}
            <div className={`mb-8 ${matchResult.winner_id === currentStudentId ? 'animate-bounce' : ''}`}>
              {matchResult.winner_id === currentStudentId ? (
                <>
                  <Trophy className="w-24 h-24 text-yellow-400 mx-auto mb-4" />
                  <h2 className="text-4xl font-bold text-yellow-400 mb-2">VITÓRIA!</h2>
                  <p className="text-white/80">Você derrotou {matchResult.loser_name}!</p>
                </>
              ) : (
                <>
                  <X className="w-24 h-24 text-red-500 mx-auto mb-4" />
                  <h2 className="text-4xl font-bold text-red-500 mb-2">DERROTA</h2>
                  <p className="text-white/80">{matchResult.winner_name} venceu o duelo</p>
                </>
              )}
            </div>

            {/* Rewards */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 max-w-md mx-auto">
              <h3 className="text-white font-bold text-lg mb-4">Resultado:</h3>
              
              <div className="space-y-3">
                {matchResult.winner_id === currentStudentId ? (
                  <>
                    <div className="flex items-center justify-between text-green-400">
                      <span>AURA Ganha:</span>
                      <span className="font-bold flex items-center gap-1">
                        <Sparkles className="w-4 h-4" />
                        +{matchResult.rewards?.winner_xp || 200}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-amber-400">
                      <span>Insígnia:</span>
                      <span className="font-bold">🏅 Conhecimento</span>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-between text-red-400">
                    <span>HP Perdido:</span>
                    <span className="font-bold flex items-center gap-1">
                      <Heart className="w-4 h-4" />
                      -{matchResult.rewards?.loser_hp_penalty || 20}
                    </span>
                  </div>
                )}
              </div>

              <Button
                onClick={() => navigate('/student/dashboard')}
                className="w-full mt-6 bg-gradient-to-r from-purple-500 to-indigo-600"
              >
                Voltar ao Dashboard
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style jsx global>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out 3;
        }
        @keyframes flash-red {
          0%, 100% { filter: none; }
          50% { filter: brightness(2) saturate(2) hue-rotate(-30deg); }
        }
        .animate-flash-red {
          animation: flash-red 0.2s ease-in-out 3;
        }
        .hover\:scale-102:hover {
          transform: scale(1.02);
        }
      `}</style>
    </div>
  );
};

export default DuelArena;
